import morph from "../helpers/morph";
import { isGiftWrapped } from "../helpers/gift-wrap";

/**
 * POST to a cart endpoint without dispatching `updateCart`, for the first half
 * of a two-step change whose second step re-renders the cart.
 */
const postCart = (url, body) =>
  fetch(`${url}.js`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).then((response) =>
    response.ok ? response.json() : Promise.reject(response),
  );

// Unwrapping blanks the gift-wrap properties rather than dropping them:
// change.js ignores an empty `properties` and keeps any key it's sent.
const UNWRAPPED = { Gift_Wrap: "", _gift_wrap_key: "" };

// A monogrammed item's monogram properties, as monogram-cart.js writes them.
// Blanked, like gift wrap's, when the monogram has left the cart.
const MONOGRAM_PROPERTIES = [
  "_uuid",
  "Item",
  "_Product",
  "Font",
  "Text",
  "Placement",
  "Size",
  "FirstColor",
  "SecondColor",
  "DropShadow",
  "_SKU",
];

// Liquid's `json` filter writes line-item properties as [key, value] pairs
const itemProperties = (item) => {
  const properties = JSON.parse(item.dataset.itemProperties || "{}");
  return Array.isArray(properties)
    ? Object.fromEntries(properties)
    : properties;
};

class CartContent extends HTMLElement {
  requestId = 1;
  sweptKeys = new Set();

  onChange = (e) => {
    const item = e.target.closest("[data-item-key]");
    if (!item) return;

    if (e.target.matches("[data-gift-wrap-toggle]")) {
      e.target.disabled = true;
      this._showGiftWrapProgress(
        item,
        e.target.checked ? "adding" : "removing",
      );
      e.target.checked ? this._addGiftWrap(item) : this._removeGiftWrap(item);
    } else if (e.target.matches(".quantity__input")) {
      this._updateQty(item, parseInt(e.target.value, 10));
    }
  };

  onClick = (e) => {
    const button = e.target.closest("[data-move-to-wishlist]");
    if (button) this._moveToWishlist(button);
  };

  onUpdateCart = (e) => {
    const requestId = e.detail.data?.requestId;

    // Ignore responses superseded by a later request.
    if (requestId === this.requestId || !requestId) {
      this._updateCart(e.detail.cart.sections[this.sectionId]);
    }
  };

  /**
   * Disables Move to Wishlist for lines already saved, as prod's Swym buttons
   * did. Matched by variant, not product, so another variant of a saved
   * product can still be moved.
   */
  renderWishlistState = () => {
    const saved = window.wishlist?.items ?? [];

    this.querySelectorAll("[data-move-to-wishlist]").forEach((button) => {
      const { productId, variantId, labelMove, labelSaved } = button.dataset;
      const inWishlist = saved.some(
        ({ empi, epi }) =>
          String(empi) === productId && String(epi) === variantId,
      );

      button.disabled = inWishlist;
      button.setAttribute("aria-label", inWishlist ? labelSaved : labelMove);
    });
  };

  connectedCallback() {
    this.addEventListener("change", this.onChange);
    this.addEventListener("click", this.onClick);
    document.addEventListener("updateCart", this.onUpdateCart);
    document.addEventListener("wishlist:change", this.renderWishlistState);
    this._removeOrphans();
    this.renderWishlistState();
    window.wishlist?.load();
  }

  disconnectedCallback() {
    this.removeEventListener("change", this.onChange);
    this.removeEventListener("click", this.onClick);
    document.removeEventListener("updateCart", this.onUpdateCart);
    document.removeEventListener("wishlist:change", this.renderWishlistState);
  }

  /**
   * A monogram's quantity follows its item's, and removing a wrapped item
   * takes its gift wrap with it.
   */
  _updateQty(item, quantity) {
    const { itemKey, giftWrapLineKey, monogramLineKey } = item.dataset;
    const removesGiftWrap = quantity === 0 && Boolean(giftWrapLineKey);

    if (!removesGiftWrap && !monogramLineKey) {
      window.changeCart({ id: itemKey, quantity }, this._requestOptions());
      return;
    }

    const updates = { [itemKey]: quantity };
    if (monogramLineKey) updates[monogramLineKey] = quantity;
    if (removesGiftWrap) updates[giftWrapLineKey] = 0;

    window.updateCart(
      {
        updates,
        ...(removesGiftWrap && {
          attributes: this._giftMessageAfterUnwrapping(item),
        }),
      },
      this._requestOptions(),
    );
  }

  /**
   * The line only leaves the cart once Swym has saved it, so a failed save
   * can't lose the item from both.
   */
  _moveToWishlist(button) {
    const item = button.closest("[data-item-key]");
    const { productId, variantId, productUrl, productTitle, imageUrl, price } =
      button.dataset;

    button.disabled = true;

    window.wishlist
      .add({
        empi: Number(productId),
        epi: Number(variantId),
        du: productUrl,
        dt: productTitle,
        iu: imageUrl,
        pr: Number(price),
      })
      .then((saved) => {
        if (saved) {
          this._updateQty(item, 0);
        } else {
          button.disabled = false;
        }
      });
  }

  /**
   * Tags the item with a fresh `_gift_wrap_key`, then adds a wrap line carrying
   * the same key. If the wrap line can't be added (sold out, say), the tag comes
   * back off so the item isn't left marked as wrapped for free.
   */
  _addGiftWrap(item) {
    const { itemKey } = item.dataset;
    const quantity = Number(item.dataset.itemQuantity);
    const properties = itemProperties(item);
    const giftWrapKey = crypto.randomUUID();

    postCart(window.routes.cart_change_url, {
      id: itemKey,
      quantity,
      properties: {
        ...properties,
        Gift_Wrap: "Yes",
        _gift_wrap_key: giftWrapKey,
      },
    })
      .then((cart) => {
        const wrappedKey = cart.items.find(
          (line) =>
            isGiftWrapped(line) &&
            line.properties._gift_wrap_key === giftWrapKey,
        )?.key;

        window.addToCart(
          {
            id: this.giftWrapVariantId,
            quantity: 1,
            properties: { _gift_wrap_key: giftWrapKey },
          },
          {
            ...this._requestOptions(),
            onError: () =>
              postCart(window.routes.cart_change_url, {
                id: wrappedKey,
                quantity,
                properties: { ...properties, ...UNWRAPPED },
              }).finally(() => this._refreshCart()),
          },
        );
      })
      .catch(() => this._refreshCart());
  }

  _removeGiftWrap(item) {
    const { itemKey, giftWrapLineKey } = item.dataset;
    const quantity = Number(item.dataset.itemQuantity);

    postCart(window.routes.cart_change_url, {
      id: itemKey,
      quantity,
      properties: { ...itemProperties(item), ...UNWRAPPED },
    })
      .then(() =>
        window.updateCart(
          {
            updates: giftWrapLineKey ? { [giftWrapLineKey]: 0 } : {},
            attributes: this._giftMessageAfterUnwrapping(item),
          },
          this._requestOptions(),
        ),
      )
      .catch(() => this._refreshCart());
  }

  /**
   * A gift wrap or monogram line outlives its item when the item leaves the
   * cart some other way (it sells out, or is removed at checkout), and would
   * still be charged, so it's removed. An item whose monogram has gone that
   * way loses its monogram properties instead, as in the legacy theme. Each
   * key is tried once, so a failed change can't loop through re-renders.
   */
  _removeOrphans() {
    const unswept = (attribute) =>
      (this.getAttribute(attribute) || "")
        .split(",")
        .filter((key) => key && !this.sweptKeys.has(key));

    const giftWrapKeys = unswept("orphaned-gift-wrap");
    const keys = [...giftWrapKeys, ...unswept("orphaned-monograms")];

    if (keys.length) {
      keys.forEach((key) => this.sweptKeys.add(key));
      window.updateCart(
        {
          updates: Object.fromEntries(keys.map((key) => [key, 0])),
          ...(giftWrapKeys.length && {
            attributes: this._giftMessageAfterUnwrapping(),
          }),
        },
        this._requestOptions(),
      );
      return;
    }

    // One at a time: each change re-renders the cart, which sweeps again.
    const item = [...this.querySelectorAll("[data-monogram-orphan]")].find(
      (orphan) => !this.sweptKeys.has(orphan.dataset.itemKey),
    );
    if (!item) return;

    this.sweptKeys.add(item.dataset.itemKey);
    window.changeCart(
      {
        id: item.dataset.itemKey,
        quantity: Number(item.dataset.itemQuantity),
        properties: {
          ...itemProperties(item),
          ...Object.fromEntries(MONOGRAM_PROPERTIES.map((key) => [key, ""])),
        },
      },
      this._requestOptions(),
    );
  }

  /** Restored by the re-render that follows, whether the change succeeds or not. */
  _showGiftWrapProgress(item, state) {
    const label = item.querySelector("[data-gift-wrap-label]");
    if (label) label.textContent = label.dataset[state];
  }

  /** The gift message belongs to the wrapped items, so it goes with the last one. */
  _giftMessageAfterUnwrapping(item) {
    const othersWrapped = [
      ...this.querySelectorAll("[data-gift-wrap-line-key]"),
    ].some((wrapped) => wrapped !== item);

    return othersWrapped ? {} : { gift_message: "" };
  }

  _requestOptions() {
    return {
      onError: () => this._refreshCart(),
      data: { requestId: ++this.requestId },
      sections: [this.sectionId],
    };
  }

  _refreshCart() {
    const url = `${window.routes.cart_url}?section_id=${this.sectionId}`;

    fetch(url)
      .then((response) => response.text())
      .then((markup) => this._updateCart(markup))
      .catch(() => console.warn(`Error fetching cart section from ${url}`));
  }

  _updateCart(markup) {
    if (!markup) return;

    const html = new DOMParser().parseFromString(markup, "text/html");
    const sourceContent = html.querySelector(".mini-cart__content");

    if (!sourceContent) return;

    this._carryGiftWrapState(sourceContent);
    morph(this, sourceContent);
    this._removeOrphans();
    // The server can't know the wishlist, so the morph resets every button.
    this.renderWishlistState();
  }

  /**
   * The server renders a gift-wrap accordion open only when its item is
   * wrapped, so a morph would snap shut one the visitor opened — or just
   * unchecked. Each keeps its current state instead. Line keys change when gift
   * wrap is toggled, so accordions are matched by variant and, for repeats of
   * one variant, by order.
   */
  _carryGiftWrapState(source) {
    const openByVariant = {};
    this.querySelectorAll("[data-gift-wrap-variant]").forEach((el) => {
      const variant = el.dataset.giftWrapVariant;
      (openByVariant[variant] ??= []).push(el.querySelector("details").open);
    });

    source.querySelectorAll("[data-gift-wrap-variant]").forEach((el) => {
      const open = openByVariant[el.dataset.giftWrapVariant]?.shift();
      if (open === undefined) return;

      const details = el.querySelector("details");
      details.open = open;
      details.classList.toggle("open", open);
    });
  }

  get sectionId() {
    return this.getAttribute("cart-section-id");
  }

  get giftWrapVariantId() {
    return this.getAttribute("gift-wrap-variant-id");
  }
}

if (!customElements.get("cart-content")) {
  customElements.define("cart-content", CartContent);
}
